import { useEffect, useState, Suspense, useCallback, useMemo, lazy, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { loadChart3DSnapshots } from '../utils/chart3dSnapshot';
import { Switch, Space, Typography, message, Modal, Input, Select, Button, Divider, Tooltip, Tabs } from 'antd';
import {
  GlobalOutlined,
  BgColorsOutlined,
  AppstoreOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  AimOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import SceneToolbar from '../components/scene/SceneToolbar';
import CameraCalibrationModal from '../components/scene/CameraCalibrationModal';
import useCalibrationStore from '../stores/calibrationStore';
import PropertyPanel from '../components/scene/PropertyPanel';
import Overlay2DPropsPanel from '../components/dashboard/Overlay2DPropsPanel';
import RegionDragOverlay from '../components/scene/RegionDragOverlay';
import RegionSelectOverlay from '../components/scene/RegionSelectOverlay';
import { formatRegion } from '../utils/displayRegion';
import PropertyTreePanel from '../components/scene/PropertyTreePanel';
import SceneRuler from '../components/scene/SceneRuler';
import useGisStore from '../stores/gisStore';
import { copySubObject, cutSubObject, pasteSubObject, deleteSelectedSubObjects, openRenameSubObject } from '../utils/subObjectOps';
import SubObjectContextMenu from '../components/scene/SubObjectContextMenu';
import ModelLoadingBar from '../components/scene/ModelLoadingBar';
import { dragHudRegistry } from '../stores/dragHudRegistry';
import AxisLockButtons from '../components/scene/AxisLockButtons';
import AppLoading from '../components/AppLoading';
import useEditorUiStore from '../stores/editorUiStore';
import { usePopupStore } from '../stores/popupStore';
import useSceneStore from '../stores/sceneStore';
import { applyMainCameraPathPose } from '../utils/mainCamera';
import useSelectionStore from '../stores/selectionStore';
import useLayerStore from '../stores/layerStore';
import useDashboardStore from '../stores/dashboardStore';
import { useMeasureStore } from '../stores/measureStore';
import { KeyframeEngine } from '../core/KeyframeEngine';
import { useEditLock } from '../hooks/useEditLock';
import { useSelectionPersistence } from '../hooks/useSelectionPersistence';
import { usePanelPersistence } from '../hooks/usePanelPersistence';
import { useSceneSave } from '../hooks/useSceneSave';
import { useBindingApply } from '../hooks/useBindingApply';
import { useEmissiveArbiter } from '../hooks/useEmissiveArbiter';
import { useVisualBindingApply } from '../hooks/useVisualBindingApply';
import { useDeviceLink3D } from '../hooks/useDeviceLink3D';
import { useSceneBindingStore, DEVICE_LINK_TYPE } from '../stores/sceneBindingStore';
import { deleteModelsByIds } from '../utils/modelActions';
import { useDataBindingStore } from '../stores/dataBindingStore';
import { fetchScene, updateScene } from '../api/scenes';
import { fetchModels, deleteModel as apiDeleteModel } from '../api/models';
import { fetchPages, createPage, updatePage, fetchPage } from '../api/pages';
import { ensureDeviceSocket } from '../services/deviceSocket';
import { broadcastFusion, subscribeFusion } from '../utils/fusionSync';
import type { Page, SceneAnchor } from '@dtwin/shared';
import '../styles/editor.css';

const SceneCanvas = lazy(() => import('../components/scene/SceneCanvas'));
const GisOverlayScene = lazy(() => import('../components/gis/GisOverlayScene'));
const DashboardEditor = lazy(() => import('./DashboardEditor'));
import DashboardComponentsOverlay from '../components/dashboard/DashboardComponentsOverlay';
import DashboardViewportEditLayer from '../components/dashboard/DashboardViewportEditLayer';
import LayerPanel2D from '../components/dashboard/LayerPanel2D';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useWatermarkFlag } from '../components/Watermark';

const { Text } = Typography;

/** 场景↔大屏绑定默认锚点：关联/新建大屏时自动写入（3D 内叠加 2D 大屏的初始位置/缩放/可见性） */
const DEFAULT_SCENE_ANCHOR: SceneAnchor = {
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: 1,
  visible: true,
};

export default function SceneEditor() {
  const { id } = useParams();
  // M8.5 A-07：相机标定向导弹窗开关（必须顶层调用，SceneEditor 存在提前返回分支）
  const calibOpen = useCalibrationStore((s) => s.isOpen);
  // ④ 项目编辑锁：同一场景仅 1 个编辑会话
  const { locked: editLocked, acquiring: lockAcquiring } = useEditLock('scene', id);
  const { gisEnabled, toggleGis, gisState, setLayers: setGisLayers } = useGisStore();

  // GIS 开关联动左侧属性定位：进入卫星地图 → 自动切到 GIS 属性面板；退出 → 恢复进入前的面板
  const handleGisToggle = (checked: boolean) => {
    const { activePanel, panelBeforeGis, setActivePanel, setPanelBeforeGis } = useEditorUiStore.getState();
    if (checked) {
      // 进入：记住进入前所在面板（若已在 GIS 面板则不覆盖记忆）
      if (activePanel !== 'gis') {
        setPanelBeforeGis(activePanel ?? null);
      }
      setActivePanel('gis');
    } else {
      // 退出：恢复进入前所在面板
      setActivePanel(panelBeforeGis ?? null);
      setPanelBeforeGis(null);
    }
    toggleGis();
  };
  const models = useSceneStore((s) => s.models);
  const selectedModelId = useSceneStore((s) => s.selectedModelId);
  // 2026-10-06：展示区域（框选提示 / 区域徽标）；预览弹窗打开时暂停主画布
  const displayRegion = useSceneStore((s) => s.displayRegion);
  const regionSelecting = useSceneStore((s) => s.regionSelecting);
  const regionGenSelecting = useSceneStore((s) => s.regionGenSelecting);
  const scenePreviewOpen = useEditorUiStore((s) => s.scenePreviewOpen);
  const selectedIds = useSelectionStore((s) => s.selectedIds);
  const currentScene = useSceneStore((s) => s.currentScene);
  const setScene = useSceneStore((s) => s.setScene);
  const setModels = useSceneStore((s) => s.setModels);
  const loadEnvironment = useSceneStore((s) => s.loadEnvironment);
  const setEffects = useSceneStore((s) => s.setEffects);
  const selectModel = useSceneStore((s) => s.selectModel);
  const undo = useSceneStore((s) => s.undo);
  const redo = useSceneStore((s) => s.redo);
  const updateModel = useSceneStore((s) => s.updateModel);
  const removeModel = useSceneStore((s) => s.removeModel);
  const setMarkers = useSceneStore((s) => s.setMarkers);
  const setFlyLines = useSceneStore((s) => s.setFlyLines);
  const setWaypoints = useSceneStore((s) => s.setWaypoints);
  const setClipping = useSceneStore((s) => s.setClipping);
  const setCarousel = useSceneStore((s) => s.setCarousel);
  const setMeasurements = useSceneStore((s) => s.setMeasurements);
  const setLayers = useLayerStore((s) => s.setLayers);
  const bindings = useDataBindingStore((s) => s.bindings);

  const [loading, setLoading] = useState(true);
  const [leftPanelWidth, setLeftPanelWidth] = useState(400);
  const [rightPanelWidth, setRightPanelWidth] = useState(300);
  // 阶段8：面板折叠（参考 2D 大屏编辑器折叠模式：40px 窄条 + 箭头展开）
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [resizing, setResizing] = useState<'left' | 'right' | null>(null);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const keyframeEngine = useMemo(() => new KeyframeEngine(), []);

  // 2026-09-14：选中态持久化——刷新后自动恢复上次选中的模型（属性面板直接可用，无需重新点选）
  useSelectionPersistence(id);
  // 2026-09-27：左侧属性面板持久化——刷新后恢复功能菜单定位（模型/环境等）与构件树展开/解锁态
  usePanelPersistence(id);
  // 2026-09-27：场景保存（保存按钮 / Ctrl+S 共用）
  const { save: saveScene } = useSceneSave();

  // ── 一体化双层编辑容器（G9/阶段1）：activeEditor 控制 2D 大屏编辑态互斥；
  //    dashboardId 提升到 editorUiStore（阶段1.5｜G10：LayerPanel 混合图层树与其共享）
  const activeEditor = useEditorUiStore((s) => s.activeEditor);
  const dashboardId = useEditorUiStore((s) => s.dashboardId);
  const setDashboardId = useEditorUiStore((s) => s.setDashboardId);
  const overlayPreview = useEditorUiStore((s) => s.overlayPreview);
  const overlayEditMode = useEditorUiStore((s) => s.overlayEditMode);
  const isDashboardEditing = activeEditor === 'dashboard' && dashboardId !== null;
  const dashPage = useDashboardStore((s) => s.currentPage);
  // 2026-10-05：就地编辑选中 2D 组件时，右侧 3D 属性区切换为 2D 组件属性（左侧菜单/图层树不受影响）
  const dashComponents = useDashboardStore((s) => s.components);
  const selectedComponentId = useDashboardStore((s) => s.selectedComponentId);
  const show2DProps = overlayEditMode && dashComponents.some((c) => c.id === selectedComponentId);
  const overlayRightTab = useEditorUiStore((s) => s.overlayRightTab);
  const setOverlayRightTab = useEditorUiStore((s) => s.setOverlayRightTab);

  // 2026-10-05：就地编辑中选中 3D 模型 → 取消 2D 选中，右侧恢复 3D 属性（用户要求：选 3D 模型看 3D 属性）
  useEffect(() => {
    if (!overlayEditMode || !selectedModelId) return;
    if (useDashboardStore.getState().selectedComponentId) {
      useDashboardStore.getState().selectComponent(null);
    }
  }, [overlayEditMode, selectedModelId]);
  // Q2：绑定弹窗提升到 editorUiStore（LayerPanel「2D 大屏图层」点击未关联时也能打开）
  const dashboardModalOpen = useEditorUiStore((s) => s.dashboardBindOpen);
  const setDashboardModalOpen = useEditorUiStore((s) => s.setDashboardBindOpen);
  // Q5/B2：免费档水印状态（3D 水印为 SceneCanvas 的 WebGL 全屏叠加层；此处提供 e2e 标记 + 顶部升级条）
  const watermarkFlag = useWatermarkFlag();
  const bannerText = usePopupStore((s) => s.config.upgrade?.bannerText) || '免费版 · 升级套餐去水印';
  const [pageList, setPageList] = useState<Page[]>([]);
  const [selPageId, setSelPageId] = useState<string | null>(null);
  const [newPageName, setNewPageName] = useState('');

  // 大屏选择预览：选中大屏后加载其页面数据（不进入编辑态），Modal 内渲染预览小窗
  const [selPagePreview, setSelPagePreview] = useState<Page | null>(null);
  useEffect(() => {
    if (!selPageId) {
      setSelPagePreview(null);
      return;
    }
    let cancelled = false;
    useDashboardStore
      .getState()
      .loadPage(selPageId)
      .then(() => {
        if (!cancelled) setSelPagePreview(useDashboardStore.getState().currentPage);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selPageId]);

  // B2：GIS 模式不允许就地编辑（开启 GIS 时自动退出编辑层）
  useEffect(() => {
    if (gisEnabled && overlayEditMode) {
      useEditorUiStore.getState().setOverlayEditMode(false);
      message.info('已开启 GIS，大屏叠加编辑已退出');
    }
  }, [gisEnabled, overlayEditMode]);

  // 阶段1.5｜G10：场景加载完成后反查 page.sceneId 自动恢复大屏关联（刷新不丢）+ 取名称/组件（不进编辑态）
  useEffect(() => {
    if (!id || dashboardId) return;
    let cancelled = false;
    fetchPages()
      .then((pages) => {
        if (cancelled) return;
        const linked = pages.find((p) => p.sceneId === id);
        if (linked) {
          setDashboardId(linked.id);
          useDashboardStore.getState().loadPage(linked.id).catch(() => {});
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, dashboardId, setDashboardId]);

  const openDashboardModal = useCallback(() => {
    setDashboardModalOpen(true);
  }, []);

  // Q3a 修复：无论从工具栏还是图层条目打开弹窗，都加载「已有大屏」列表（原仅工具栏路径加载 → 图层入口打开时下拉为空）
  useEffect(() => {
    if (!dashboardModalOpen) return;
    setSelPageId(null);
    setNewPageName('');
    fetchPages()
      .then((list) => setPageList(list))
      .catch(() => setPageList([]));
  }, [dashboardModalOpen]);

  /** 关联大屏时若尚无锚点则写入默认锚点（可视化图层初始位置/可见性） */
  const ensureSceneAnchor = useCallback(async (pageId: string) => {
    try {
      const pg = await fetchPage(pageId);
      if (pg && !pg.sceneAnchor) {
        await updatePage(pageId, { sceneAnchor: DEFAULT_SCENE_ANCHOR });
      }
    } catch {
      // 锚点初始化失败不阻塞关联流程
    }
  }, []);

  const attachAndEnter = useCallback(async (pageId: string) => {
    if (!id) return;
    try {
      await updatePage(pageId, { sceneId: id });
      await ensureSceneAnchor(pageId);
      await useDashboardStore.getState().loadPage(pageId);
      setDashboardId(pageId);
      useEditorUiStore.getState().enterDashboardEditor();
      setDashboardModalOpen(false);
      broadcastFusion({ type: 'page-scene-linked', pageId, sceneId: id, source: 'scene' });
      message.success('已关联大屏并进入 2D 编辑');
    } catch {
      message.error('关联/加载大屏失败');
    }
  }, [id]);

  // 仅关联并保存（不进入 2D 编辑）：关联后场景即有 2D 大屏叠加，可在图层查看/预览，"大屏编辑"按钮随时可进
  const attachOnly = useCallback(async (pageId: string) => {
    if (!id) return;
    try {
      await updatePage(pageId, { sceneId: id });
      await ensureSceneAnchor(pageId);
      await useDashboardStore.getState().loadPage(pageId);
      setDashboardId(pageId);
      setDashboardModalOpen(false);
      broadcastFusion({ type: 'page-scene-linked', pageId, sceneId: id, source: 'scene' });
      message.success('已关联大屏（可在「大屏编辑」进入 2D 设计）');
    } catch {
      message.error('关联大屏失败');
    }
  }, [id]);

  const createDashboardAndEnter = useCallback(async () => {
    const name = newPageName.trim();
    if (!name) {
      message.warning('请输入大屏名称');
      return;
    }
    if (!id) return;
    try {
      const page = await createPage({ name, sceneId: id, sceneAnchor: DEFAULT_SCENE_ANCHOR });
      await useDashboardStore.getState().loadPage(page.id);
      setDashboardId(page.id);
      useEditorUiStore.getState().enterDashboardEditor();
      setDashboardModalOpen(false);
      broadcastFusion({ type: 'page-scene-linked', pageId: page.id, sceneId: id, source: 'scene' });
      message.success('已新建大屏并进入 2D 编辑');
    } catch {
      message.error('创建大屏失败');
    }
  }, [id, newPageName]);

  // Q3c：跨标签页同步——大屏编辑器侧关联/解除后，本页无需刷新即更新 2D 大屏图层
  useEffect(() => {
    if (!id) return;
    return subscribeFusion((msg) => {
      if (msg.source === 'scene') return; // 本侧发起的忽略
      const st = useEditorUiStore.getState();
      if (msg.sceneId === id) {
        if (st.dashboardId !== msg.pageId) {
          st.setDashboardId(msg.pageId);
          useDashboardStore.getState().loadPage(msg.pageId).catch(() => {});
          message.info('已同步：本场景在大屏编辑器侧关联了 2D 大屏');
        }
      } else if (msg.sceneId === null && st.dashboardId === msg.pageId) {
        st.setDashboardId(null);
        message.info('已同步：大屏已解除与本场景的关联');
      }
    });
  }, [id]);

  // 将数据绑定评估结果应用到3D模型
  useBindingApply();
  // 预取大屏编辑器 chunk：进入 2D 大屏编辑免首次下载等待（首次进入加载慢的主要来源）
  useEffect(() => {
    import('./DashboardEditor').catch(() => {});
  }, []);
  // 阶段3｜3.4：emissive 通道统一仲裁（告警>选中>悬停>数据变色）
  useEmissiveArbiter();
  // 阶段5：视觉规则绑定应用（点位→材质 color 实时驱动）
  useVisualBindingApply();
  // 阶段3｜3.1/3.2：设备联动（3D 点击桥接 → selectDevice；2D 选中 → 3D 高亮/飞行）
  useDeviceLink3D();

  // 阶段2｜设备数据中枢：场景编辑器内启动设备实时订阅（懒单例，断线内置重连）
  useEffect(() => {
    ensureDeviceSocket();
  }, []);

  // 主流编辑器快捷键（参考 Three.js Editor / Blender）：
  // - Ctrl/Cmd + S 保存场景（2026-09-27：拦截浏览器"保存网页"默认弹窗；输入框聚焦时同样生效）
  // - Ctrl/Cmd + A 全选所有模型
  // - Delete / Backspace 删除当前选中的模型集合
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // 大屏编辑激活：3D 编辑器快捷键整体锁定
      if (useEditorUiStore.getState().activeEditor === 'dashboard') return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void saveScene();
        return;
      }
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      // 2026-10-13（用户反馈）：新增变换快捷键——选中模型/部件后 E=移动 / R=旋转 / T=缩放
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        const st = useSceneStore.getState();
        const hasSel = !!st.selectedModelId || st.selectedSubObjects.length > 0;
        const k = e.key.toLowerCase();
        if (hasSel && (k === 'e' || k === 'r' || k === 't')) {
          e.preventDefault();
          st.setTransformMode(k === 'e' ? 'translate' : k === 'r' ? 'rotate' : 'scale');
          message.info(k === 'e' ? '移动模式（E）' : k === 'r' ? '旋转模式（R）' : '缩放模式（T）');
          return;
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        const all = useSceneStore.getState().models.map((m) => m.id);
        if (all.length > 0) {
          useSelectionStore.getState().selectAll(all);
          message.success(`已全选 ${all.length} 个模型`);
        }
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // 2026-09-14 修复：部件选中（单选/多选）时跳过——交给 handleKeyDown 的部件删除分支，
        // 否则"选中部件按 Delete"会误删整个模型（selectionStore 残留模型 id）
        const subSt = useSceneStore.getState();
        if (subSt.selectedSubObjects.length > 0 || subSt.selectedSubObject) return;
        const ids = useSelectionStore.getState().selectedIds;
        if (ids.length > 0) {
          e.preventDefault();
          deleteModelsByIds(ids);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [saveScene]);

  // 面板拖拽
  const handleMouseDown = useCallback((panel: 'left' | 'right') => (e: React.MouseEvent) => {
    setResizing(panel);
    dragStartX.current = e.clientX;
    dragStartWidth.current = panel === 'left' ? leftPanelWidth : rightPanelWidth;
    e.preventDefault();
  }, [leftPanelWidth, rightPanelWidth]);

  useEffect(() => {
    if (!resizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - dragStartX.current;
      const newWidth = dragStartWidth.current + delta;
      if (resizing === 'left') {
        setLeftPanelWidth(Math.max(300, Math.min(500, newWidth)));
      } else {
        setRightPanelWidth(Math.max(260, Math.min(400, -newWidth)));
      }
    };
    const handleMouseUp = () => setResizing(null);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (useEditorUiStore.getState().activeEditor === 'dashboard') return;
    const tagName = (e.target as HTMLElement).tagName;
    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return;

    // 2026-09-14（阶段 B）：解锁 + 选中部件时，小部件快捷键优先（对齐 Blender/Unity）
    const sub = useSceneStore.getState().selectedSubObject;
    if (sub) {
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'c') { e.preventDefault(); copySubObject(sub.modelId, sub.uuid); return; }
      if (mod && k === 'x') { e.preventDefault(); cutSubObject(sub.modelId, sub.uuid); return; }
      if (mod && k === 'v') { e.preventDefault(); pasteSubObject(); return; }
      if (e.key === 'F2') { e.preventDefault(); openRenameSubObject(sub.modelId, sub.uuid); return; }
      // 多选（如选中同源部件）时 Delete 批量删除全部选中
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSelectedSubObjects(); return; }
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
      e.preventDefault();
      redo();
    }
    if (e.key === 'Delete') {
      const selId = useSceneStore.getState().selectedModelId;
      if (selId) {
        removeModel(selId);
        apiDeleteModel(selId).catch(() => {});
        message.success('已删除');
      }
    }
  }, [undo, redo, removeModel]);

  const handleAnimationApply = useCallback((targetId: string, property: string, value: number[]) => {
    const parts = property.split('.');
    if (parts[0] === 'position' || parts[0] === 'rotation' || parts[0] === 'scale') {
      const axis = parts[1] as 'x' | 'y' | 'z';
      const idx = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
      const current = useSceneStore.getState().models.find(m => m.id === targetId);
      if (current) {
        const arr = (current as any)[parts[0]].slice() as number[];
        arr[idx] = value[0];
        useSceneStore.getState().updateModel(targetId, { [parts[0]]: arr } as any, false);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      keyframeEngine.destroy();
    };
  }, [handleKeyDown, keyframeEngine]);

  // 阶段4｜G3：场景加载后拉取绑定，device-link 回填 models.deviceId（3D 联动持久恢复）
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    useSceneBindingStore
      .getState()
      .load(id)
      .then(() => {
        if (cancelled) return;
        const links = useSceneBindingStore
          .getState()
          .bindings.filter((b) => b.visualType === DEVICE_LINK_TYPE && b.deviceId);
        if (links.length === 0) return;
        useSceneStore.getState().setModels(
          useSceneStore.getState().models.map((m) => {
            const link = links.find((l) => l.modelNodeId === m.id);
            return link?.deviceId ? { ...m, deviceId: link.deviceId } : m;
          }),
        );
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    Promise.all([fetchScene(id), fetchModels(id)])
      .then(([scene, models]) => {
        setScene(scene as any);
        setModels(models as any);
        const cfg = (scene as any).config || {};
        if (cfg.environment) useSceneStore.getState().loadEnvironment(cfg.environment);
        if (cfg.effects) setEffects(cfg.effects);
        if (cfg.boundedMode !== undefined) useSceneStore.getState().setBoundedMode(cfg.boundedMode);
      if (cfg.displayRegion !== undefined) useSceneStore.getState().setDisplayRegion(cfg.displayRegion ?? null);
      if (cfg.pivotOffsets && typeof cfg.pivotOffsets === 'object') useSceneStore.getState().setPivotOffsets(cfg.pivotOffsets);
        if (cfg.showGrid !== undefined) useSceneStore.getState().setShowGrid(cfg.showGrid);
        if (cfg.showGround !== undefined) useSceneStore.getState().setShowGround(cfg.showGround);
        if (cfg.markers) setMarkers(cfg.markers);
        if (cfg.flyLines) setFlyLines(cfg.flyLines);
        if (cfg.waypoints) setWaypoints(cfg.waypoints);
        if (cfg.browseRoute) useSceneStore.getState().setBrowseRoute(cfg.browseRoute);
        if (cfg.carousel) setCarousel(cfg.carousel);
        if (cfg.clipping) setClipping(cfg.clipping);
        if (cfg.measurements) {
          setMeasurements(cfg.measurements);
          // 同步到新测量 store（场景加载后恢复测量记录）
          useMeasureStore.getState().setList(
            (cfg.measurements as Array<{
              id: string; type?: string; mode?: string; points: Array<{ position: { x: number; y: number; z: number } }>;
              result?: Record<string, number>; visible?: boolean;
            }>).map((m) => ({
              id: m.id,
              mode: (m.mode || m.type || 'distance') as 'distance' | 'angle' | 'area' | 'boxSize',
              points: m.points.map((p) => ({ x: p.position.x, y: p.position.y, z: p.position.z })),
              result: m.result || {},
              visible: m.visible !== false,
            })),
          );
        }
        if (cfg.layers) setLayers(cfg.layers);
        if (cfg.pathCameras) useSceneStore.getState().setPathCameras(cfg.pathCameras);
        if (cfg.lights) useSceneStore.getState().lights = cfg.lights;
        // 2026-10-17（体积元素 S1）
        if (cfg.volumes) useSceneStore.getState().volumes = cfg.volumes;
        if (cfg.annotations) useSceneStore.getState().annotations = cfg.annotations;
        useSceneStore.getState().setSpikeChart3D?.((cfg.spikeChart3D as never) ?? null);
        useSceneStore.getState().setSpikeBoard3D?.((cfg.spikeBoard3D as never) ?? null);
        useSceneStore.getState().setSpikeHud3D?.((cfg.spikeHud3D as never) ?? null);
        loadChart3DSnapshots(cfg.chart3dSnapshots);
        useSceneStore.getState().setChart3DObjects?.((cfg.chart3dObjects as never) ?? []);
        if (cfg.cameraStrategy) useSceneStore.getState().setCameraStrategy(cfg.cameraStrategy);
        if (cfg.savedCamera) useSceneStore.getState().setSavedCamera(cfg.savedCamera);
        // 2026-10-15（P0-④）：鸟瞰相机 + 主相机（旧场景无字段 → 保持默认 editor/关闭）
        if (cfg.birdsEyeCamera) useSceneStore.getState().setBirdsEyeCamera(cfg.birdsEyeCamera);
        if (cfg.mainCamera) useSceneStore.getState().setMainCamera(cfg.mainCamera);
        // 2026-10-16（P1-3）：主相机=漫游相机 → 应用其位姿（custom + savedCamera；编辑器/预览/发布同源）
        applyMainCameraPathPose(cfg as never, useSceneStore.getState() as never);
        // 2026-09-27（审计 FE-F08 修复）：数据源与属性绑定随场景恢复（原仅内存，刷新丢失）
        if (cfg.datasources || cfg.bindings) {
          useDataBindingStore.getState().initDocument({
            datasources: (cfg.datasources as any[]) ?? [],
            nodes: Object.entries((cfg.bindings as Record<string, any[]>) ?? {}).map(([nodeId, list]) => ({
              id: nodeId,
              bindings: list,
            })),
          } as any);
        }
        // 2026-09-27（审计 FE-H08 修复）：GIS 图层/围栏/热力图/视图随场景恢复（原仅内存）
        if (cfg.gis) {
          useGisStore.getState().restore(cfg.gis as any);
        }
        // 加载完成：重置 dirty，避免初始化 setEnvironment/setEffects 等误标未保存
        useSceneStore.getState().setDirty(false);
      })
      .catch(() => message.error('加载场景失败'))
      .finally(() => setLoading(false));
  }, [id, setScene, setModels, loadEnvironment, setEffects, setMarkers, setFlyLines, setWaypoints, setClipping, setCarousel, setMeasurements, setLayers]);

  if (loading) {
    return <AppLoading />;
  }

  const selectedModel = models.find((m) => m.id === selectedModelId);
  const bindingCount = Object.values(bindings).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <DndProvider backend={HTML5Backend}>
    <div className="editor-container">
      {/* ④ 项目编辑锁：被他人占用时只读提示 */}
      {!editLocked && !lockAcquiring && (
        <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#722ed1', color: '#fff', padding: '4px 16px', borderRadius: 4, fontSize: 13 }}>
          该项目正在被其他用户编辑，当前为只读模式
        </div>
      )}
      {/* 2D 大屏编辑激活：3D 工具栏/面板隐藏（display:none 常驻挂载，返回秒回、无布局重排闪烁） */}
      <div style={{ display: isDashboardEditing ? 'none' : 'block' }}>
        <SceneToolbar />
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {leftCollapsed ? (
          <div style={{ width: 40, background: '#1a1a2e', borderRight: '1px solid #30475e', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, flexShrink: 0 }}>
            <Tooltip title="展开属性面板"><Button size="small" type="text" icon={<DoubleRightOutlined />} onClick={() => setLeftCollapsed(false)} /></Tooltip>
            <AppstoreOutlined style={{ color: '#888', fontSize: 18, marginTop: 12 }} />
          </div>
        ) : (
        <div className="editor-panel" style={{ width: leftPanelWidth, minWidth: 300, maxWidth: 500, flexShrink: 0, display: isDashboardEditing ? 'none' : 'flex' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 6px 0', flexShrink: 0 }}>
            <Button size="small" type="text" icon={<DoubleLeftOutlined />} onClick={() => setLeftCollapsed(true)} title="收起" />
          </div>
          <PropertyTreePanel
            selectedModelId={selectedModelId || null}
            keyframeEngine={keyframeEngine}
          />
        </div>
        )}

        {!isDashboardEditing && (
          <div
            className="editor-resize-handle"
            onMouseDown={handleMouseDown('left')}
            style={{ userSelect: resizing ? 'none' : undefined }}
          />
        )}

        <div className="editor-center-viewport" style={{ flex: 1, minWidth: 0, position: 'relative', background: '#2a2a3c' }}>
          {/* B2：免费档顶部升级提示条（点击弹套餐订阅；WebGL 水印由 SceneCanvas 叠加层绘制） */}
          {watermarkFlag && (
            <div
              data-testid="watermark-upgrade-banner"
              onClick={() => usePopupStore.getState().openUpgrade()}
              style={{
                position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 120,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 14px', borderRadius: 16, fontSize: 12, color: '#ffd666',
                background: 'rgba(20,20,32,0.85)', border: '1px solid rgba(250,173,20,0.55)', userSelect: 'none',
              }}
            >
              <ThunderboltOutlined style={{ fontSize: 12 }} />
              <span>{bannerText}</span>
              <ArrowRightOutlined style={{ fontSize: 10 }} />
            </div>
          )}
          {/* 2026-10-06：展示区域框选遮罩层（DOM 级真实虚线框；任意漫游模式可用） */}
          {regionSelecting && !isDashboardEditing && <RegionDragOverlay />}
      {/* G2：区域生成框选（框选模型集合 → AI 批量操作） */}
      {regionGenSelecting && !isDashboardEditing && <RegionSelectOverlay />}
          {/* 2026-10-06：展示区域框选提示 / 区域徽标 */}
          {regionSelecting && (
            <div
              data-testid="display-region-hint"
              style={{
                position: 'absolute', top: 44, left: '50%', transform: 'translateX(-50%)', zIndex: 121,
                padding: '4px 14px', borderRadius: 16, fontSize: 12, color: '#ffd666',
                background: 'rgba(20,20,32,0.85)', border: '1px solid rgba(250,173,20,0.55)', userSelect: 'none',
              }}
            >
              鼠标左键拖拽框选展示区域（区域外将被裁剪；Esc 取消）
            </div>
          )}
          {displayRegion && !regionSelecting && (
            <div
              data-testid="display-region-badge"
              style={{
                position: 'absolute', top: 44, right: 12, zIndex: 121,
                padding: '3px 10px', borderRadius: 14, fontSize: 12, color: '#5cdbd3',
                background: 'rgba(20,20,32,0.85)', border: '1px solid rgba(19,194,194,0.55)',
                display: 'flex', alignItems: 'center', gap: 6, userSelect: 'none',
              }}
            >
              展示区域 {formatRegion(displayRegion)}
              <Button
                size="small"
                type="link"
                data-testid="display-region-clear-hud"
                style={{ fontSize: 12, padding: 0, height: 16 }}
                onClick={() => useSceneStore.getState().setDisplayRegion(null)}
              >
                清除
              </Button>
            </div>
          )}
          {/* 右上操作区（scene 态） */}
          {!isDashboardEditing && (
            <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 100 }}>
              <Space>
                <Button size="small" icon={<AppstoreOutlined />} onClick={openDashboardModal}>
                  导入大屏
                </Button>
                {dashboardId && (
                  <Tooltip title={gisEnabled
                    ? 'GIS 模式下不可用大屏叠加编辑，请先关闭 GIS'
                    : '大屏叠加编辑：直接在大屏叠加层上选中/移动/缩放/旋转组件，Alt 交还相机；左侧组件库、右侧属性/图层'}>
                    <Button
                      size="small"
                      data-testid="enter-overlay-edit"
                      icon={<AimOutlined />}
                      disabled={gisEnabled}
                      onClick={() => useEditorUiStore.getState().setOverlayEditMode(true)}
                    >
                      大屏叠加编辑
                    </Button>
                  </Tooltip>
                )}
                <Text style={{ color: '#fff', fontSize: 12 }}>GIS 模式</Text>
                <Switch
                  data-testid="gis-switch"
                  checked={gisEnabled}
                  onChange={handleGisToggle}
                  checkedChildren={<GlobalOutlined />}
                  unCheckedChildren={<BgColorsOutlined />}
                />
              </Space>
            </div>
          )}

          {/* 2026-09-14：模型加载进度条（重载贴图/大模型加载——保持模型与镜头不变） */}
          <ModelLoadingBar />

          {/* 2026-09-14：拖拽数值 HUD（对齐 Blender 变换中数值显示——模块级 DOM 直更，零重渲染） */}
          <div
            ref={(el) => { dragHudRegistry.el = el; }}
            style={{
              position: 'fixed',
              bottom: 84,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 3600,
              display: 'none',
              background: 'rgba(15,15,26,0.92)',
              border: '1px solid #30475e',
              borderRadius: 6,
              padding: '6px 12px',
              color: '#9ecbff',
              fontSize: 12,
              fontFamily: 'monospace',
              pointerEvents: 'none',
              whiteSpace: 'pre',
            }}
          />
          {/* 2026-09-14：拖动轴锁按钮（X 红/Y 绿/Z 蓝，对齐 Blender；与拖动中快捷键共用状态） */}
          {!isDashboardEditing && !gisEnabled && !overlayEditMode && <AxisLockButtons />}

          {/* 2026-09-14（阶段 B）：小部件右键上下文菜单（视口/树共用，fixed 定位） */}
          <SubObjectContextMenu />

          {/* 3D 常驻层：永不卸载（进出 2D 大屏编辑不重新加载 3D 场景/模型，大模型场景不再出现加载闪烁） */}
          <SceneRuler width={2000} height={1600} scale={0.6}>
            <Suspense fallback={<AppLoading />}>
              {gisEnabled ? (
                <GisOverlayScene
                  layers={gisState.layers}
                  models={models}
                  selectedModelId={selectedModelId}
                  onModelClick={(modelId) => selectModel(modelId)}
                />
              ) : (
                <SceneCanvas sceneId={id} interactionLocked={isDashboardEditing} paused={scenePreviewOpen} />
              )}
            </Suspense>
          </SceneRuler>

          {/* 2D 大屏编辑覆盖层（3D 背景保持挂载 + 交互锁定，退出编辑秒回，无重载） */}
          {isDashboardEditing && dashboardId && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 20, background: '#1e1e2e' }}>
              <Suspense fallback={<AppLoading />}>
                <DashboardEditor pageId={dashboardId} embedded />
              </Suspense>
            </div>
          )}

          {/* 阶段1.5｜G10：只读预览叠加（scene 态点击图层树大屏条目触发）：仅叠加 2D 大屏组件层（透明背景，
              3D 场景保持挂载可见可操作——指针事件全透传，2D 大屏内容不可交互），不再嵌入 SceneCanvas 重复加载 3D
              2026-09-08｜B-05/B-06：叠加层应用 sceneAnchor 锚点（偏移/缩放/可见性） */}
          {!isDashboardEditing && !overlayEditMode && dashboardId && overlayPreview && dashPage && id && dashPage.sceneAnchor?.visible !== false && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 200, pointerEvents: 'none' }}>
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  transform: dashPage.sceneAnchor
                    ? `translate(${dashPage.sceneAnchor.position.x}px, ${dashPage.sceneAnchor.position.y}px) scale(${dashPage.sceneAnchor.scale})`
                    : undefined,
                  transformOrigin: 'center',
                }}
              >
                <DashboardComponentsOverlay
                  components={dashComponents}
                  pageWidth={dashPage.config?.width || 1920}
                  pageHeight={dashPage.config?.height || 1080}
                  backgroundColor={dashPage.config?.backgroundColor}
                  backgroundImage={dashPage.config?.backgroundImage}
                />
              </div>
              <Button
                size="small"
                style={{ position: 'absolute', top: 12, right: 12, zIndex: 210, pointerEvents: 'auto' }}
                onClick={() => useEditorUiStore.getState().setOverlayPreview(false)}
              >
                退出预览
              </Button>
            </div>
          )}
          {/* 阶段B2：3D 视口内大屏叠加编辑层（scene 态；独立编辑窗口，Alt 交还相机）
              DndProvider 提升到编辑器顶层（左侧组件库 useDrag 与编辑层 useDrop 共用） */}
          {!isDashboardEditing && dashboardId && overlayEditMode && id && dashPage && (
            <DashboardViewportEditLayer sceneId={id} />
          )}

          {/* Q5：网格水印 e2e 断言标记（实际水印在 SceneCanvas 地面 4×4 渲染） */}
          {watermarkFlag && <div data-testid="scene-grid-watermark" style={{ display: 'none' }} />}
        </div>

        <div style={{ display: isDashboardEditing ? 'none' : 'block' }}>
          <div
            className="editor-resize-handle"
            onMouseDown={handleMouseDown('right')}
            style={{ userSelect: resizing ? 'none' : undefined }}
          />
        </div>

                {rightCollapsed ? (
          <div style={{ width: 40, background: '#1a1a2e', borderLeft: '1px solid #30475e', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, flexShrink: 0 }}>
            <Tooltip title="展开属性面板"><Button size="small" type="text" icon={<DoubleLeftOutlined />} onClick={() => setRightCollapsed(false)} /></Tooltip>
            <AppstoreOutlined style={{ color: '#888', fontSize: 18, marginTop: 12 }} />
          </div>
        ) : (
        <div className="editor-panel" style={{ width: rightPanelWidth, minWidth: 260, maxWidth: 400, flexShrink: 0, borderRight: 'none', borderLeft: '1px solid #30475e', display: isDashboardEditing ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 6px 0', flexShrink: 0 }}>
            <Button size="small" type="text" icon={<DoubleRightOutlined />} onClick={() => setRightCollapsed(true)} title="收起" />
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {overlayEditMode ? (
              <Tabs
                size="small"
                activeKey={overlayRightTab}
                onChange={(k) => setOverlayRightTab(k as 'props' | 'layers')}
                tabBarStyle={{ marginBottom: 0, paddingLeft: 8 }}
                data-testid="ov-right-tabs"
                items={[
                  {
                    key: 'props',
                    label: '属性配置',
                    children: show2DProps ? <Overlay2DPropsPanel /> : <PropertyPanel />,
                  },
                  { key: 'layers', label: '图层', children: <LayerPanel2D /> },
                ]}
              />
            ) : (
              <PropertyPanel />
            )}
          </div>
        </div>
        )}
      </div>

      <div className="editor-status-bar">
        <Space size={16}>
          <span>场景: {currentScene?.name || '未命名'}</span>
          <span>模型: {models.length} 个</span>
          <span>已选: {selectedIds.length > 0 ? `${selectedIds.length} 个${selectedModel ? ` · ${selectedModel.name}` : ''}` : '无'}</span>
        </Space>
        <Space size={16}>
          <span>缩放: 60%</span>
          <span>
            坐标: {selectedModel?.position ? selectedModel.position.map((v) => v.toFixed(2)).join(', ') : '0, 0, 0'}
          </span>
          <span>绑定: {bindingCount}</span>
          <span>GIS: {gisEnabled ? '启用' : '禁用'}</span>
          <span>编辑: {isDashboardEditing ? '2D大屏' : '3D场景'}</span>
        </Space>
      </div>

      {/* 大屏导入/新建（G9/阶段1）：支持「仅关联并保存」（不进 2D 编辑）与「关联并进入 2D 编辑」 */}
      <Modal
        title="3D 场景关联 2D 大屏"
        open={dashboardModalOpen}
        onCancel={() => setDashboardModalOpen(false)}
        footer={null}
        width={480}
      >
        <div style={{ marginBottom: 8, color: '#888', fontSize: 12 }}>导入已设计好的大屏（关联后场景可展示/激活编辑）</div>
        <Space style={{ width: '100%' }} orientation="vertical">
          <Select
            style={{ width: '100%' }}
            placeholder="选择已有大屏"
            value={selPageId ?? undefined}
            onChange={setSelPageId}
            options={pageList.map((p) => ({
              label: `${p.name}${p.sceneId ? '（已关联场景）' : ''}`,
              value: p.id,
            }))}
          />

          {/* 选中大屏的预览小窗：确认是否想要的大屏，无需进入 2D 设计 */}
          <div style={{ height: 220, border: '1px solid #30475e', borderRadius: 6, overflow: 'hidden', background: '#15151f', position: 'relative' }}>
            {selPagePreview ? (
              <DashboardComponentsOverlay
                components={selPagePreview.components}
                pageWidth={selPagePreview.config?.width || 1920}
                pageHeight={selPagePreview.config?.height || 1080}
                backgroundColor={selPagePreview.config?.backgroundColor}
                backgroundImage={selPagePreview.config?.backgroundImage}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666', fontSize: 12 }}>
                选择大屏后此处显示预览
              </div>
            )}
          </div>

          <Space style={{ width: '100%' }} orientation="vertical" size={4}>
            <Button block disabled={!selPageId} onClick={() => selPageId && attachOnly(selPageId)}>
              仅关联并保存（不进 2D 设计）
            </Button>
            <Button type="primary" block disabled={!selPageId} onClick={() => selPageId && attachAndEnter(selPageId)}>
              关联并进入 2D 编辑
            </Button>
          </Space>
        </Space>
        <Divider />
        <div style={{ marginBottom: 8, color: '#888', fontSize: 12 }}>或在此新建一个大屏</div>
        <Space style={{ width: '100%' }} orientation="vertical">
          <Input
            placeholder="新大屏名称"
            value={newPageName}
            onChange={(e) => setNewPageName(e.target.value)}
            onPressEnter={createDashboardAndEnter}
          />
          <Button onClick={createDashboardAndEnter}>新建大屏并进入 2D 编辑</Button>
        </Space>
      </Modal>
      {/* M8.5 A-07：相机标定向导（工具入口：数据▼ → 相机标定） */}
      <CameraCalibrationModal open={calibOpen} onClose={() => useCalibrationStore.getState().close()} />
    </div>
    </DndProvider>
  );
}
